// 主视图容器（EP-004-S1）—— 组合场景 / 小狗 / 顶部条 / 状态卡 / 出发按钮，按 phase 切换渲染。
// 老板 2026-09-15 反馈：away 态太单调 + 顶部按钮无效 → 顶部条全 phase 渲染并接通，
// 模糊文案升级为状态卡样式并加中文粗粒度倒计时，新增设置面板。
import { useState } from 'react';
import { useGameStore, selectPhase } from '../store';
import { computeElapsed } from '../core/timer';
import { now } from '../core/time';
import { completion, progressText } from '../domain/inventory/selectors';
import { fuzzyStatusText, remainingCountdownText } from './fuzzyStatus';
import { homeStatusTextFromSave } from './homeStatus';
import { SceneView } from './SceneView';
import { DogView } from './DogView';
import { TopBar } from './TopBar';
import { StatusCard } from './StatusCard';
import { DepartButton } from './DepartButton';
import { RevealOverlay } from './RevealOverlay';
import { SpaceView } from './SpaceView';
import { SettingsPanel } from './SettingsPanel';
import { DebugPanel } from '../ui/debug/DebugPanel';
import { isDebugMode } from '../core/debugParams';

export function MainView() {
  const phase = useGameStore(selectPhase);
  const save = useGameStore((s) => s.save);
  const storageState = useGameStore((s) => s.storageState);
  const clock = useGameStore((s) => s.clock); // 订阅以触发模糊文案重渲染
  const view = useGameStore((s) => s.view);
  const revealSnapshot = useGameStore((s) => s.revealSnapshot);
  const dogLeaving = useGameStore((s) => s.dogLeaving);
  const endDogLeave = useGameStore((s) => s.endDogLeave);
  const beginReveal = useGameStore((s) => s.beginReveal);
  const endReveal = useGameStore((s) => s.endReveal);
  const closeSpace = useGameStore((s) => s.closeSpace);
  const openSpace = useGameStore((s) => s.openSpace);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // 揭晓演出：先落盘后演出，礼物已在 inventory，覆盖全屏（EP-007-S4）。
  if (view === 'reveal' && revealSnapshot) {
    return <RevealOverlay snapshot={revealSnapshot} onDone={endReveal} />;
  }
  // 空间背包：独立全屏滚动容器（EP-008-S3）。
  if (view === 'space') {
    return <SpaceView onClose={closeSpace} />;
  }

  // 高版本存档：只读、不重置，提示用户（GDD 02 T8）。
  if (storageState === 'futureVersion') {
    return (
      <div className="main-view">
        <SceneView />
        <div className="notice">
          这份存档来自更新的版本，当前无法读取。请更新到最新版本后再继续。
        </div>
      </div>
    );
  }

  const showDog = phase === 'home' || phase === 'awaitingReveal';
  const showTopBar = save !== null; // away 也渲染（老板反馈：离家也要能进空间/设置）

  // 离家里：模糊档位文案 + 粗粒度倒计时（中文数字，B1）
  let fuzzy = '';
  let eta = '';
  if (phase === 'away' && save?.trip) {
    const { progress } = computeElapsed(save.trip.departureTimestamp, now(), save.trip.travelDurationMs);
    // FUZZY_TEXT 是**相对进度**语义（刚出门 / 走了一会儿 /…），不受时长影响，保持原调用。
    fuzzy = fuzzyStatusText(progress);
    // 🆕 P6-POLISH-ENG-002（07 §A.8）：倒计时改由**绝对剩余毫秒**推导。
    //   旅行时长升到最长 6h 后，按 progress 比例分档会显示「还要一个多钟头」这种谎报。
    const remainingMs = Math.max(0, save.trip.travelDurationMs * (1 - progress));
    eta = remainingCountdownText(remainingMs);
  }

  // 状态卡：home / awaitingReveal
  let statusText = '';
  let statusClickable = false;
  if (phase === 'home') {
    // 🆕 P6-POLISH-ENG-002（07 §C）：不再写死两句，改由 tripCount 派生轮换（11 条池）。
    statusText = homeStatusTextFromSave(save);
  } else if (phase === 'awaitingReveal') {
    // awaitingReveal **不轮换** —— 这是功能性提示（点了能揭晓），不是氛围文案，必须固定。
    statusText = '它回来了，在门口等你';
    statusClickable = true;
  }

  // 🆕 STEP 5-B：未读明信片角标 = mailbox 中 isRead === false 的条目数。
  //   路上寄出时 isRead: false（store.tick 触发），详情页打开后转 true（markPostcardRead）。
  const unreadPostcardCount = save?.mailbox.filter((p) => !p.isRead).length ?? 0;
  // 🆕 v2.1：未读礼物 = inventory 中 isRead === false 的 stack 数。
  const unreadGiftCount = save?.inventory.filter((s) => !s.isRead).length ?? 0;
  // TopBar 空间按钮合计未读数
  const totalUnread = unreadGiftCount + unreadPostcardCount;

  // 🆕 P6-POLISH-ENG-002（07 §D）：「拾起些东西 / 还没拾起」是内部术语，老板看不懂。
  //   改为五档「带回…」中文文案，**零阿拉伯数字**（B1）；数据源仍是 completion()（只反映礼物，不含明信片）。
  const { owned, denominator } = completion(save?.inventory ?? []);
  const topProgressText = progressText(owned, denominator);

  return (
    <div className="main-view">
      <SceneView />
      {showDog && (
        <DogView leaving={dogLeaving} onLeaveEnd={endDogLeave} />
      )}
      {showTopBar && (
        <TopBar
          progressText={topProgressText}
          totalUnread={totalUnread}
          // ⚠️ 必须包一层箭头函数，不能直接写 `onOpenSpace={openSpace}`。
          // openSpace 的签名是 `(initialTab?: SpaceTab) => void`，直接挂到 onClick 时，
          // React 会把 MouseEvent 当作 initialTab 传进去（TS 允许这种赋值，编译期不报错），
          // 于是 spaceTab 被写成一个 Event 对象 → 礼物/明信片两个 tab 都不匹配 →
          // 包裹打开了却什么都不渲染（老板反馈：「先展示一个空，还要手动点一下礼物」）。
          // 见 P6-POLISH-ENG-001；store.openSpace 侧已加白名单兜底，但调用点必须先修对。
          onOpenSpace={() => openSpace('gifts')}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      )}
      {fuzzy && (
        <div className="fuzzy-status">
          <span className="fuzzy-status__text">{fuzzy}</span>
          {eta && <span className="fuzzy-status__eta">{eta}</span>}
        </div>
      )}
      {statusText && (
        <StatusCard
          text={statusText}
          clickable={statusClickable}
          onClick={statusClickable ? beginReveal : undefined}
          ariaLabel={statusClickable ? '小狗回来了，在门口等你。点开查看它带回了什么。' : undefined}
          unreadPostcardCount={unreadPostcardCount}
          onPostcardBadgeClick={() => {
            // 跳到空间明信片 tab（任务规约：点击圆点 → 直接跳到空间明信片 tab）。
            openSpace('postcards');
          }}
        />
      )}
      <DepartButton />
      {storageState === 'storageUnavailable' && (
        <div className="storage-banner">当前无法保存进度，关闭页面后进度会丢失。</div>
      )}
      {settingsOpen && <SettingsPanel onClose={() => setSettingsOpen(false)} />}
      {/* clock 仅用于订阅触发重渲染 */}
      <span hidden aria-hidden="true">
        {clock}
      </span>
      {/* Debug panel: playtest only, returns null in production / when ?debug=1 missing */}
      {isDebugMode() && <DebugPanel />}
    </div>
  );
}
